/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Loader, Center, Text, Select, Stack, Group, Badge, Tabs } from "@mantine/core";
import { IconCalendar, IconSchool, IconUsersGroup } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import {
  useGetCalendarRecord,
  CalendarDepartmentScope,
} from "api/calendar/getholidayCalendar";
import UpdateOrgModal from "features/admin-settings/components/YourOrgModal";
import { useUser } from "hooks/useUser";
import { useGetOrg } from "api/organizations/getOrg";
import { useGetCalendarTimeConfig } from "api/calendar/getWorkingHoursCalender";
import { useNavigate } from "react-router-dom";
import { useGetUserPermissions } from "api/permission/getpermission";

// Roles that may switch between department calendars; everyone else is
// always auto-scoped to their own department by the server regardless of
// what's selected here. Mirrors the server's isPrivilegedForDepartmentScope.
const PRIVILEGED_SCOPE_ROLES = ["admin", "superadmin", "chief"];

const SCOPE_LABELS: Record<CalendarDepartmentScope, string> = {
  band: "Band Employees",
  school: "School Employees",
  org: "Organization",
};

interface TimeConfigItem {
  date?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
}

interface Holiday {
  title: string;
  date: string;
}
const calendarStyles = `
  .fc-event {
    cursor: pointer;
    border: none !important;
    padding: 2px 4px !important;
  }
  
  .fc-daygrid-day-events {
    min-height: 2em !important;
  }
  
  .custom-event-content {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    padding: 2px 4px;
    border-radius: 4px;
  }
  
  .event-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }
  
  .holiday-dot {
    background-color: #eab308;
    box-shadow: 0 0 0 1px rgba(234, 179, 8, 0.3);
  }
  
  .default-work-dot {
    background-color: #22c55e;
    box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.3);
  }
  
  .custom-work-dot {
    background-color: #3b82f6;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.3);
  }
  
  .fc-daygrid-day-frame {
    min-height: 100px !important;
  }
  
  .fc-day-today {
    background-color: rgba(34, 197, 94, 0.05) !important;
  }
  
  .holiday-cell {
    background-color: rgba(234, 179, 8, 0.05);
  }
`;

const CalendarView: React.FC = () => {
  const currentDate = new Date();
  const { user } = useUser();
  const userId = user?._id ?? "";
  const navigate = useNavigate();

  const [year, setYear] = useState<number>(currentDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(
    currentDate.getMonth() + 1,
  );

  const { data: permResp } = useGetUserPermissions(userId);
  const permissions = permResp?.data?.permissions ?? {};

  // Only admin/superadmin/chief can pick a different department's calendar;
  // everyone else is always auto-scoped to their own department server-side,
  // so leaving this as undefined for them is correct, not just a default.
  const isPrivilegedForScope = PRIVILEGED_SCOPE_ROLES.includes(
    (user?.role as string | undefined)?.toLowerCase() ?? "",
  );
  const [scopeOverride, setScopeOverride] = useState<
    CalendarDepartmentScope | undefined
  >(undefined);
  const effectiveScopeParam = isPrivilegedForScope ? scopeOverride : undefined;

  const {
    data: holidayData,
    isLoading,
    error,
  } = useGetCalendarRecord(year, effectiveScopeParam);

  const {
    data: timeConfigData,
    isLoading: timeConfigLoading,
    error: isConfigError,
  }: any = useGetCalendarTimeConfig(currentMonth, year, effectiveScopeParam);

  // Server-resolved scope for the data actually returned (source of truth —
  // reflects the caller's own department when no override is in play).
  const resolvedScope = holidayData?.scope ?? null;

  const [opened, setOpened] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const { data: org }: any = useGetOrg(user?.org?._id);

  // ===============================
  // Permission Keys
  // ===============================
  const ORG_PERMISSION = "organization_management.organizationUpdate";
  const HOLIDAY_PERMISSION = "holidays";

  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc: any, key: string) => {
      return acc && acc[key] !== undefined ? acc[key] : undefined;
    }, obj);
  };

  const hasPermission = (permissionKey: string) => {
    const value = getNested(permissions, permissionKey);

    return (
      value === true ||
      (typeof value === "object" &&
        value !== null &&
        Object.values(value).some(Boolean))
    );
  };

  const showAccessDenied = () => {
    showNotification({
      title: "Access Denied",
      message: "You don't have permission to perform this action.",
      color: "red",
    });
  };

  // ===============================
  // Default Working Hours
  // ===============================
  const defaultWorkingHours = {
    startTime: "08:00",
    endTime: "17:00",
    totalHours: 9,
  };

  const hasSpecificConfig = (dateStr: string): boolean => {
    return (
      timeConfigData?.some((item: TimeConfigItem) => item.date === dateStr) ||
      false
    );
  };

  // ===============================
  // Default Working Hours from API
  // ===============================
  const getWorkingHoursForDate = (dateStr: string): TimeConfigItem => {
    if (!timeConfigData || timeConfigData.length === 0) {
      // Fallback if no API data at all
      return {
        startTime: "08:00",
        endTime: "17:00",
        totalHours: 9,
      };
    }

    // First check for specific date config
    const specificConfig = timeConfigData.find(
      (item: TimeConfigItem) => item.date === dateStr,
    );

    if (specificConfig) {
      return specificConfig;
    }

    // Find the default config (item without a date)
    const defaultConfig = timeConfigData.find(
      (item: TimeConfigItem) => !item.date,
    );

    // Return default config if found, otherwise first item as fallback
    return defaultConfig || timeConfigData[0];
  };

  // ===============================
  // Generate Calendar Events
  // ===============================
  const generateCalendarEvents = useMemo(() => {
    const events: any[] = [];
    const holidaysMap = new Map();

    holidayData?.data?.forEach((holiday: Holiday) => {
      holidaysMap.set(holiday.date, holiday);
    });

    const daysInMonth = new Date(year, currentMonth, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(currentMonth).padStart(
        2,
        "0",
      )}-${String(day).padStart(2, "0")}`;

      const holiday = holidaysMap.get(dateStr);

      if (holiday) {
        events.push({
          title: holiday.title,
          date: dateStr,
          color: "#eab308",
          textColor: "#ffffff",
          isHoliday: true,
        });
      } else {
        const workingHours = getWorkingHoursForDate(dateStr);
        const isDefaultTime = !hasSpecificConfig(dateStr);

        events.push({
          title: `Work: ${workingHours.startTime} - ${workingHours.endTime} (${workingHours.totalHours}h)`,
          date: dateStr,
          color: isDefaultTime ? "#22c55e" : "#3b82f6",
          textColor: "#ffffff",
          isHoliday: false,
        });
      }
    }

    return events;
  }, [timeConfigData, holidayData, currentMonth, year]);

  // ===============================
  // Click Handlers
  // ===============================
  const handleEditClick = () => {
    if (!org?._id) return;
    setSelectedOrgId(org._id);
    setOpened(true);
  };

  const handleDateClick = (arg: any) => {
    const clickedDate = arg.dateStr;
    const event = generateCalendarEvents.find((e) => e.date === clickedDate);

    if (event?.isHoliday) {
      if (hasPermission(HOLIDAY_PERMISSION)) {
        navigate("/holiday");
      } else {
        showAccessDenied();
      }
    } else {
      if (hasPermission(ORG_PERMISSION)) {
        handleEditClick();
      } else {
        showAccessDenied();
      }
    }
  };

  const handleEventClick = (arg: any) => {
    const eventData = arg.event.extendedProps;

    if (eventData.isHoliday) {
      if (hasPermission(HOLIDAY_PERMISSION)) {
        navigate("/holiday");
      } else {
        showAccessDenied();
      }
    } else {
      if (hasPermission(ORG_PERMISSION)) {
        handleEditClick();
      } else {
        showAccessDenied();
      }
    }
  };

  const handleDatesSet = (arg: any) => {
    const viewStart = arg.start;
    const viewEnd = arg.end;

    const visibleDate = new Date((viewStart.getTime() + viewEnd.getTime()) / 2);

    const month = visibleDate.getMonth() + 1;
    const currentYear = visibleDate.getFullYear();

    if (month !== currentMonth || currentYear !== year) {
      setCurrentMonth(month);
      setYear(currentYear);
    }
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [
      (currentYear - 1).toString(),
      currentYear.toString(),
      (currentYear + 1).toString(),
    ];
  }, []);

  if (isLoading || timeConfigLoading)
    return (
      <Center style={{ height: "80vh" }}>
        <Loader size="lg" />
      </Center>
    );

  if (error || isConfigError)
    return (
      <Center style={{ height: "80vh" }}>
        <Text color="red" fw={500}>
          Failed to load calendar data
        </Text>
      </Center>
    );

  const initialCalendarDate = new Date(year, currentMonth - 1, 1);

  return (
    <>
      <style>{calendarStyles}</style>
      <Stack p="md" bg="white" spacing="md">
        {isPrivilegedForScope && (
          <Tabs
            value={scopeOverride ?? resolvedScope ?? "org"}
            onTabChange={(val) =>
              setScopeOverride((val as CalendarDepartmentScope) ?? undefined)
            }
          >
            <Tabs.List>
              <Tabs.Tab value="org" icon={<IconCalendar size={14} />}>
                Calendar
              </Tabs.Tab>
              <Tabs.Tab value="school" icon={<IconSchool size={14} />}>
                School
              </Tabs.Tab>
              <Tabs.Tab value="band" icon={<IconUsersGroup size={14} />}>
                Band
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
        )}

        <Group position="apart" align="flex-end">
          <Group align="flex-end" spacing="md">
            <Select
              label="Select Year"
              placeholder="Pick year"
              data={yearOptions}
              value={year.toString()}
              onChange={(val) => {
                const newYear = Number(val);
                setYear(newYear);
                setCurrentMonth(currentDate.getMonth() + 1);
              }}
              style={{ maxWidth: 180 }}
            />

            {resolvedScope && (
              <Badge color="teal" size="lg" variant="light">
                Viewing: {SCOPE_LABELS[resolvedScope]}
              </Badge>
            )}
          </Group>

          {/* Legend */}
          <Group spacing="lg">
            <Group spacing="xs">
              <div className="event-dot holiday-dot" />
              <Text size="sm" fw={500}>
                Holiday
              </Text>
            </Group>
            <Group spacing="xs">
              <div className="event-dot default-work-dot" />
              <Text size="sm" fw={500}>
                Default Hours
              </Text>
            </Group>
            <Group spacing="xs">
              <div className="event-dot custom-work-dot" />
              <Text size="sm" fw={500}>
                Custom Hours
              </Text>
            </Group>
          </Group>
        </Group>

        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          timeZone="local"
          initialView="dayGridMonth"
          initialDate={initialCalendarDate}
          headerToolbar={{
            start: "prev,next today",
            center: "title",
            end: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          events={generateCalendarEvents}
          dayMaxEvents
          height="80vh"
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
        />

        {selectedOrgId && (
          <UpdateOrgModal
            opened={opened}
            orgId={selectedOrgId}
            onClose={() => setOpened(false)}
          />
        )}
      </Stack>
    </>
  );
};

export default CalendarView;
